import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

interface Activity {
    id: string;
    text: string;
    time: string; // This will be formatted to relative time
    type: 'patient' | 'doctor' | 'appointment' | 'critical' | 'system';
    entityId?: number | string;
    navigateTo?: string;
    search?: string;
}

interface NotificationsContextType {
    recentActivities: Activity[];
    setRecentActivities: (activities: Activity[]) => void;
    unreadCount: number;
    markAllRead: () => void;
    dashboardStats: any;
    todayAppointments: any[];
    setTodayAppointments: React.Dispatch<React.SetStateAction<any[]>>;
    fetchDashboardSummary: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export function NotificationsProvider({ children }: { children: ReactNode }) {
    const { user } = useAuth();
    const [recentActivities, setRecentActivities] = useState<Activity[]>([]);
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [dashboardStats, setDashboardStats] = useState<any>(null);
    const [todayAppointments, setTodayAppointments] = useState<any[]>([]);
    const lastSeenCountRef = useRef<number>(0);

    const markAllRead = useCallback(() => {
        lastSeenCountRef.current = recentActivities.length;
        setUnreadCount(0);
    }, [recentActivities.length]);

    // Update unread count whenever recentActivities changes
    useEffect(() => {
        const newCount = Math.max(0, recentActivities.length - lastSeenCountRef.current);
        setUnreadCount(newCount);
    }, [recentActivities]);

    // Helper to format relative time
    const getRelativeTime = (timestamp: string) => {
        if (!timestamp) return 'Just now';
        
        // Append Z if missing to treat as UTC
        const ts = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
        const activityDate = new Date(ts);
        const now = new Date();
        const diffMs = now.getTime() - activityDate.getTime();
        
        if (isNaN(diffMs)) return timestamp; // Fallback to raw string if invalid

        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    };

    const fetchNotifications = useCallback(async () => {
        if (!user || user.role !== 'superadmin') return;
        
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            // Fetch consolidated summary (which includes counts and activities)
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin/dashboard/summary`, config);
            
            if (res.data.status === 'Success') {
                // 1. Process Counts/Stats
                if (res.data.counts) {
                    setDashboardStats(res.data.counts);
                }

                // 2. Process Today's Appointments
                const appointmentsData = res.data.appointments || res.data.today_appointments;
                if (Array.isArray(appointmentsData)) {
                    const formattedAppointments = appointmentsData.map((apt: any) => {
                        // Combine date and time to create a valid Date object for formatting
                        const appointmentDateTime = new Date(`${apt.appointment_date}T${apt.appointment_time}`);
                        return {
                            ...apt,
                            date: appointmentDateTime,
                            time: appointmentDateTime
                        };
                    });
                    setTodayAppointments(formattedAppointments);
                }

                // 3. Process Activities
                if (Array.isArray(res.data.activities)) {
                    const processedActivities = res.data.activities.map((activity: any) => {
                        const rawType = (activity.type || '').toLowerCase();
                        let navigateTo = activity.navigateTo || '';
                        let search = '';
                        let entityId = activity.entityId;

                        // Normalize type
                        const normalizedType = rawType.includes('patient') ? 'patient' : rawType;

                        // Normalize navigateTo to valid frontend routes
                        if (normalizedType === 'patient' || navigateTo.startsWith('/patients')) {
                            // Extract entityId from path if navigateTo is like /patients/abc123
                            if (navigateTo.startsWith('/patients/') && navigateTo.length > 10) {
                                const idFromPath = navigateTo.split('/').pop();
                                if (idFromPath) entityId = idFromPath;
                            }
                            // Extract patient name from text (e.g. "Patient John Doe joined")
                            if (activity.text && activity.text.startsWith('Patient ')) {
                                search = activity.text.replace('Patient ', '').replace(' joined', '').trim();
                            }
                            navigateTo = '/patients';
                        } else if (normalizedType === 'doctor' || navigateTo.startsWith('/doctors')) {
                            if (navigateTo.startsWith('/doctors/') && navigateTo.length > 9) {
                                const idFromPath = navigateTo.split('/').pop();
                                if (idFromPath) entityId = idFromPath;
                            }
                            navigateTo = '/doctors';
                        } else if (normalizedType === 'critical' || navigateTo.startsWith('/critical-patients') || navigateTo.startsWith('/patient-status-list?status=critical')) {
                            navigateTo = '/patient-status-list?status=critical&activity=heartrate';
                        } else if (normalizedType === 'appointment' || navigateTo.startsWith('/appointments')) {
                            navigateTo = '/appointments';
                        } else if (!navigateTo) {
                            // Fallback based on type
                            if (rawType.includes('patient')) navigateTo = '/patients';
                            else if (rawType.includes('doctor')) navigateTo = '/doctors';
                            else if (rawType.includes('appointment')) navigateTo = '/appointments';
                            else if (rawType.includes('critical')) navigateTo = '/patient-status-list?status=critical&activity=heartrate';
                        }
                        
                        return {
                            ...activity,
                            type: normalizedType,
                            time: getRelativeTime(activity.time),
                            navigateTo,
                            entityId,
                            search
                        };
                    });
                    setRecentActivities(processedActivities);
                }
            }
        } catch (err) {
            console.error('NotificationsContext: Error fetching notifications:', err);
        }
    }, [user]);

    // Poll for notifications independently (only for superadmin)
    useEffect(() => {
        if (!user || user.role !== 'superadmin') return;

        // Initial fetch
        fetchNotifications();
    }, [user, fetchNotifications]);

    return (
        <NotificationsContext.Provider value={{ 
            recentActivities, 
            setRecentActivities, 
            unreadCount, 
            markAllRead, 
            dashboardStats,
            todayAppointments,
            setTodayAppointments,
            fetchDashboardSummary: fetchNotifications
        }}>
            {children}
        </NotificationsContext.Provider>
    );
}

export function useNotifications() {
    const context = useContext(NotificationsContext);
    if (context === undefined) {
        throw new Error('useNotifications must be used within a NotificationsProvider');
    }
    return context;
}
